import styled from '@emotion/styled';
import Dropdown from '@comp/Dropdown/Dropdown';
import Tick from 'aquilla/icons/actions/Tick';
import { useState } from 'react';
import { mediaQuery } from '@styles/index';
import { CenterSelectionData } from './useTestSeriesData';

// #region Styles

const FoundTestCenterContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  border-radius: 8px;
  border: 1px solid var(--color-divider);

  ${mediaQuery.uptoMobile} {
    gap: 0;
  }
`;

const FoundTestCenterTitle = styled.div`
  font-weight: 700;
  font-size: 12px;
  line-height: 12px;
  letter-spacing: 2%;
  text-transform: uppercase;
  color: var(--color-text-secondary);
  border-bottom: 1px solid var(--color-divider);
  display: flex;
  justify-content: start;
  align-items: center;
  padding: 16px;
`;

const FoundTestCenterList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 16px;
  max-height: 400px;
  overflow-y: auto;

  ${mediaQuery.uptoMobile} {
    padding: 8px 0;
    gap: 0px;
    max-height: 200px;
  }
`;

const FoundTestCenterItem = styled.div<{
  type: 'find-nearest-test-center' | 'select-test-center';
}>`
  display: flex;
  flex-direction: row;
  gap: 10px;
  width: 100%;
  padding: 16px;
  cursor: ${({ type }) =>
    type === 'find-nearest-test-center' ? 'default' : 'pointer'};
  justify-content: space-between;
`;

const FoundTestCenterItemLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const FoundTestCenterItemRight = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 24px;
  height: 24px;

  ${mediaQuery.uptoMobile} {
    width: 16px;
    height: 16px;
  }
`;

const FoundTestCenterItemTitle = styled.p`
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0%;
  color: var(--color-text-primary);

  ${mediaQuery.uptoMobile} {
    font-weight: 400;
  }
`;

const FoundTestCenterItemSubtitle = styled.div`
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0%;
  display: flex;
  flex-direction: row;
  gap: 4px;
  color: var(--color-text-secondary);
`;

const CenterSelectionFlowWrapper = styled.div<{
  type: 'find-nearest-test-center' | 'select-test-center';
}>`
  width: ${({ type }) =>
    type === 'find-nearest-test-center' ? '50%' : '100%'};
  display: flex;
  flex-direction: column;
  gap: 10px;

  ${mediaQuery.uptoMobile} {
    width: 100%;
  }
`;

const DropdownContainer = styled.div<{
  type: 'find-nearest-test-center' | 'select-test-center';
}>`
  display: flex;
  flex-direction: ${({ type }) =>
    type === 'find-nearest-test-center' ? 'row' : 'column'};
  gap: 10px;

  ${mediaQuery.uptoMobile} {
    flex-direction: column;
    margin-top: ${({ type }) =>
      type === 'find-nearest-test-center' ? '40px' : '0px'};
  }
`;
// #endregion

export const useDropdown = ({
  centerList
}: {
  centerList: CenterSelectionData[];
}) => {
  const [selectedState, setSelectedState] = useState<{
    label: string;
    value: string;
  } | null>(null);
  const [selectedCity, setSelectedCity] = useState<{
    label: string;
    value: string;
  } | null>(null);
  const [selectedCenter, setSelectedCenter] = useState<{
    label: string;
    value: string;
  } | null>(null);

  const stateOptions = [
    ...new Set((centerList || []).map((item) => item.state))
  ].map((state) => ({
    label: state,
    value: state
  }));

  const cityOptions =
    selectedState !== null
      ? [
          ...new Set(
            (centerList || [])
              .filter((item) => item.state === selectedState.value)
              .map((item) => item.city)
          )
        ].map((city) => ({ label: city, value: city }))
      : [];

  const centerOptions =
    selectedState && selectedCity
      ? (centerList || [])
          .filter(
            (item) =>
              item.state === selectedState.value &&
              item.city === selectedCity.value
          )
          .map((center) => ({
            label: `${center.test_center_name ?? ''} ${
              center.test_centre_address ?? ''
            }`.trim(),
            value: `${center.test_center_name ?? ''} ${
              center.test_centre_address ?? ''
            }`.trim()
          }))
      : [];

  return {
    stateOptions,
    cityOptions,
    centerOptions,
    selectedState,
    selectedCity,
    selectedCenter,
    setSelectedState,
    setSelectedCity,
    setSelectedCenter
  };
};
const FoundTestCentersSection = ({
  type,
  centerOptions,
  selectedState,
  selectedCity,
  selectedCenter,
  setSelectedCenter,
  statesUnavailable
}: {
  type: 'find-nearest-test-center' | 'select-test-center';
  centerOptions: Option[];
  selectedState: Option | null;
  selectedCity: Option | null;
  selectedCenter: Option | null;
  setSelectedCenter: (center: Option) => void;
  statesUnavailable?: boolean;
}) => {
  if (!statesUnavailable && (!selectedState || !selectedCity)) {
    return null;
  }

  return (
    <FoundTestCenterContainer>
      <FoundTestCenterTitle>
        WE FOUND {centerOptions.length} TEST CENTRES IN YOUR AREA
      </FoundTestCenterTitle>
      <FoundTestCenterList>
        {centerOptions.length > 0 ? (
          centerOptions.map((center) => (
            <FoundTestCenterItem
              type={type}
              key={center.value}
              onClick={() => {
                if (type === 'find-nearest-test-center') {
                  return;
                }
                setSelectedCenter({
                  label: center.label,
                  value: center.value
                });
              }}
            >
              <FoundTestCenterItemLeft>
                <FoundTestCenterItemTitle
                  style={{
                    fontWeight:
                      selectedCenter?.value === center.value ? 600 : 400
                  }}
                >
                  {center.label}
                </FoundTestCenterItemTitle>
                <FoundTestCenterItemSubtitle>
                  {selectedState?.value && <p>{selectedState.value}</p>}
                  {selectedCity?.value && <p>•</p>}
                  {selectedCity?.value && <p>{selectedCity.value}</p>}
                </FoundTestCenterItemSubtitle>
              </FoundTestCenterItemLeft>
              <FoundTestCenterItemRight>
                {selectedCenter?.value === center.value ? <Tick /> : null}
              </FoundTestCenterItemRight>
            </FoundTestCenterItem>
          ))
        ) : (
          <FoundTestCenterItem type={type}>
            <FoundTestCenterItemTitle>
              No test centres found
            </FoundTestCenterItemTitle>
          </FoundTestCenterItem>
        )}
      </FoundTestCenterList>
    </FoundTestCenterContainer>
  );
};

type Option = {
  label: string;
  value: string;
};
const CenterSelectionFlow = ({
  type,
  stateOptions,
  cityOptions,
  centerOptions,
  selectedState,
  selectedCity,
  selectedCenter,
  setSelectedState,
  setSelectedCity,
  setSelectedCenter
}: {
  type: 'find-nearest-test-center' | 'select-test-center';
  stateOptions: Option[];
  cityOptions: Option[];
  centerOptions: Option[];
  selectedState: Option | null;
  selectedCity: Option | null;
  selectedCenter: Option | null;
  setSelectedState: (state: Option) => void;
  setSelectedCity: (city: Option) => void;
  setSelectedCenter: (center: Option) => void;
}) => {
  return (
    <CenterSelectionFlowWrapper type={type}>
      {stateOptions.length ? (
        <DropdownContainer type={type}>
          <Dropdown
            // @ts-ignore
            buttonWidth="100%"
            options={stateOptions}
            selectedOption={selectedState}
            isDisabled={!stateOptions.length}
            onOptionClick={(option) => {
              setSelectedCity(null);
              setSelectedCenter(null);
              setSelectedState(option);
            }}
            placeholder="Select a state"
            showIcon={false}
          />
          <Dropdown
            // @ts-ignore
            buttonWidth="100%"
            options={cityOptions}
            selectedOption={selectedCity}
            isDisabled={!cityOptions.length || !selectedState}
            onOptionClick={(option) => {
              setSelectedCenter(null);
              setSelectedCity(option);
            }}
            placeholder="Select a city"
            showIcon={false}
          />
        </DropdownContainer>
      ) : null}
      <FoundTestCentersSection
        type={type}
        statesUnavailable={!stateOptions.length}
        centerOptions={centerOptions}
        selectedState={selectedState}
        selectedCity={selectedCity}
        selectedCenter={selectedCenter}
        setSelectedCenter={setSelectedCenter}
      />
    </CenterSelectionFlowWrapper>
  );
};
export default CenterSelectionFlow;
